<template>
    <LMap :center="centerLatLng"
          :zoom="zoom"
          :options="{zoomSnap: 0.25, preferCanvas: true, zoomControl: false, maxZoom: 21}"
          class="h-100"
          @ready="onMapReady">
        <!-- The controls sit on the left so that the details panel of the overlay has the whole right side. -->
        <LControlLayers position="topleft" @ready="onLayersControlReady"></LControlLayers>
        <MapyCzBasicLayer/>
        <MapyCzOrthoPhotoLayer :visible="false"/>
        <LControlZoom position="topleft"/>
    </LMap>
</template>

<script lang="ts">
import MapyCzBasicLayer from '@/components/maps/MapyCzBasicLayer.vue';
import MapyCzOrthoPhotoLayer from '@/components/maps/MapyCzOrtophotoLayer.vue';
import {publishLeafletMap, unpublishLeafletMap} from '@/shared/leafletMapRegistry';
import {LControlLayers, LControlZoom, LMap} from '@vue-leaflet/vue-leaflet';
import L from 'leaflet';
import {computed, defineComponent, onBeforeUnmount, PropType} from 'vue';

interface Coordinates {
    readonly latitude: number;
    readonly longitude: number;
}

/**
 * A base map of its own for an overlay shown on a page or in a dialog (the crime map, the flood
 * map): the Mapy.cz base and aerial layers, a layers control and nothing else. The overlay
 * attaches to it through the Leaflet map registry.
 */
export default defineComponent({
    components: {
        LMap,
        LControlLayers,
        LControlZoom,
        MapyCzBasicLayer,
        MapyCzOrthoPhotoLayer,
    },
    props: {
        center: {
            type: Object as PropType<Coordinates>,
            required: true,
        },
        zoom: {
            type: Number,
            required: true,
        },
    },
    setup(props) {
        let leafletMap: L.Map | null = null;
        let layersControl: L.Control.Layers | null = null;
        let resizeObserver: ResizeObserver | null = null;

        const centerLatLng = computed((): L.LatLng => L.latLng(props.center.latitude, props.center.longitude));

        // The map and its layers control get ready in either order; the overlay needs both.
        function publish(): void {
            if (leafletMap !== null && layersControl !== null) {
                publishLeafletMap(leafletMap.getContainer(), leafletMap, layersControl);
            }
        }

        function onMapReady(mapInstance: L.Map): void {
            leafletMap = mapInstance;
            // In a dialog the map is created before the dialog is shown, with no size at all; it
            // redraws for every size it gets.
            resizeObserver = new ResizeObserver(() => mapInstance.invalidateSize());
            resizeObserver.observe(mapInstance.getContainer());
            publish();
        }

        function onLayersControlReady(layersControlInstance: L.Control.Layers): void {
            layersControl = layersControlInstance;
            publish();
        }

        onBeforeUnmount(() => {
            resizeObserver?.disconnect();
            if (leafletMap !== null) {
                unpublishLeafletMap(leafletMap.getContainer());
            }
        });

        return {centerLatLng, onMapReady, onLayersControlReady};
    },
});
</script>
